import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import {
  Autocomplete,
  Chip,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import InputField from "../../../../Components/Masters/InputField";
import SelectField from "../../../../Components/Masters/SelectField";
import EditDialog from "../../../../Components/Common/EditDialog";
import { post, put } from "../../../../utils/api";
import AddIcon from "@mui/icons-material/Add";
import AddTable from "./AddTables";
import {
  productCategoryOptions,
  typeofemissions,
} from "../../../../utils/constant";
import { Delete } from "@mui/icons-material";
import DeleteRowDialog from "../../../../Components/Common/DeleteRowDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FUEL_TYPE_OPTIONS as energyTypeOptions,
  FUEL_UNIT_BY_TYPE,
} from "../../../../utils/fuelConstants";

const SOLD_UNIT_OPTIONS = ["Pieces", "MT", "KL"];

/** Normalize for comparing API vs dropdown labels (NBSP, ZWSP, run spaces, case). */
function normalizeForUnitMatch(s) {
  return String(s ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Unit list for a sample basis key (matches `simpleUnits` objects from List.jsx). */
function getSampleBasisUnitOptions(sampleBasis, simpleUnits) {
  const basis = String(sampleBasis ?? "").trim();
  if (!basis || !Array.isArray(simpleUnits)) return [];
  const group = simpleUnits.find((u) => {
    const k = Object.keys(u)[0];
    return k === basis || String(k).trim() === basis;
  });
  if (!group) return [];
  const key = Object.keys(group)[0];
  const list = group[key];
  return Array.isArray(list) ? list : [];
}

/** Map saved product `unit` to the exact option string so MUI Select can display it. */
function resolveSampleUnitToOption(savedUnit, unitOptions) {
  const saved = savedUnit == null ? "" : String(savedUnit);
  // Keep raw saved value until `simpleUnits` has loaded (empty options); avoids wiping edit state.
  if (!unitOptions?.length) {
    return normalizeForUnitMatch(saved) ? saved : "";
  }
  if (!normalizeForUnitMatch(saved)) return "";
  const n = normalizeForUnitMatch(saved);
  const exact = unitOptions.find((u) => String(u) === saved);
  if (exact) return exact;
  const byNorm = unitOptions.find((u) => normalizeForUnitMatch(u) === n);
  return byNorm || "";
}

function Add({
  inputData,
  fetchData,
  id,
  element,
  data,
  allProductsForDuplicateCheck = [],
  setAddData,
  addData,
  simpleUnits,
  setsnackOpen,
  addUrl,
  updateUrl,
}) {
  const productsListForIdCheck =
    allProductsForDuplicateCheck?.length > 0
      ? allProductsForDuplicateCheck
      : data || [];
  const [errors, setErrors] = useState({});
  const [units, setUnits] = useState(["MT"]);
  const [processTimeout, setProcessTimeout] = useState(null);
  console.log("1111111111111", errors);
  const initialState = {
    date:
      id && element
        ? new Date(element?.date)?.toISOString()?.split("T")[0]
        : new Date().toISOString().split("T")[0],
    skipManufacturingProcess:
      id && element ? element?.skipManufacturingProcess || false : false,
    skipManufacturingProcessReason:
      id && element ? element?.skipManufacturingProcessReason || "" : "",
    productName: id && element ? element?.productName : "",
    productId: id && element ? (element?.productId ?? "") : "",
    sampleBasis: id && element ? String(element?.sampleBasis || "").trim() : "",
    sampleQuantity:
      id &&
      element &&
      element?.sampleQuantity !== undefined &&
      element?.sampleQuantity !== null &&
      String(element.sampleQuantity).trim() !== ""
        ? String(element.sampleQuantity)
        : "",
    energyUsedForMaintenance:
      id && element
        ? (element?.energyUsedForMaintenance ??
          element?.averageAnnnualEnergyConsumption ??
          element?.sampleQuantity ??
          "")
        : "",
    energyUsedForMaintenanceUnit: "Mwh",
    usePhaseEnergyConsumption:
      id && element
        ? (element?.usePhaseEnergyConsumption ??
          element?.averageAnnnualEnergyConsumption ??
          "")
        : "",
    usePhaseEnergyConsumptionUnit: "Mwh",
    unit:
      id && element
        ? resolveSampleUnitToOption(
            element?.unit,
            getSampleBasisUnitOptions(element?.sampleBasis, simpleUnits),
          ) ||
          (element?.unit != null && String(element.unit).trim() !== ""
            ? String(element.unit)
            : "MWh")
        : "MWh",
    processes:
      id && element && Array.isArray(element?.processes)
        ? element.processes.map((proc) => ({
            processName: proc.processName || "",
            airEmissions: proc.airEmissions || [],
            emissions: Array.isArray(proc.emissions)
              ? proc.emissions.map((e) => ({
                  emissionsName: e.emissionsName || "",
                  unit: e.unit || "",
                  quantity: e.quantity || "",
                }))
              : [{ emissionsName: "", unit: "", quantity: "" }],
          }))
        : [
            {
              processName: "",
              airEmissions: [],
              emissions: [{ emissionsName: "", unit: "", quantity: "" }],
            },
          ],
    category:
      id && element ? element?.category || element?.productCategory || "" : "",
    description:
      id && element
        ? element?.description || element?.productDescription || ""
        : "",
    soldUnitQuantity: "",
    soldUnit:
      id && element
        ? (() => {
            const su = element?.soldUnit;
            if (su != null && typeof su === "object") {
              return su.unit ?? "";
            }
            if (su != null && su !== "") {
              return String(su);
            }
            return element?.soldUnitType ?? element?.soldUnitUnit ?? "";
          })()
        : "",
    weight:
      id && element
        ? (element?.weight ?? element?.averageWeightPerUnit ?? "")
        : "",
    frequency:
      id && element
        ? (element?.frequency ?? element?.frequencyOfMaintenancePerYear ?? "")
        : "",
    disposalMethod: id && element ? element?.disposalMethod || "" : "",
    lifetime:
      id && element
        ? (element?.lifetime ??
          element?.expectedLifetimeOfTheProductYears ??
          "")
        : "",
    fuelEntries:
      id && element
        ? Array.isArray(element?.fuelEntries) && element.fuelEntries.length > 0
          ? element.fuelEntries.map((fuel) => ({
              fuelName: fuel?.fuelName ?? "",
              quantity: fuel?.quantity ?? "",
              unit: fuel?.unit ?? "",
            }))
          : [
              {
                fuelName: element?.fuelName ?? "",
                quantity: element?.quantity ?? "",
                unit: element?.unit ?? "",
              },
            ]
        : [{ fuelName: "", quantity: "", unit: "" }],
    usePhaseFuelEntries:
      id && element
        ? Array.isArray(element?.usePhaseFuelEntries) &&
          element.usePhaseFuelEntries.length > 0
          ? element.usePhaseFuelEntries.map((fuel) => ({
              fuelName: fuel?.fuelName ?? "",
              quantity: fuel?.quantity ?? "",
              unit: fuel?.unit ?? "",
            }))
          : [{ fuelName: "", quantity: "", unit: "" }]
        : [{ fuelName: "", quantity: "", unit: "" }],
    fuelName: id && element ? element?.fuelName ?? "" : "",
    quantity: id && element ? element?.quantity ?? "" : "",
    values: id && element ? element?.values || {} : {},
  };
  const [openDialog, setOpenDialog] = React.useState(false);
  const [formData, setFormData] = useState(initialState);
  // Time state
  const [table, setTable] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [formData2, setFormData2] = useState(initialState);
  const [processEmission, setProcessEmission] = useState([
    {
      processName: "",
      emissions: [{ emissionsName: "", unit: "", quantity: "" }],
    },
  ]);
  const [removeIndex, setRemoveIndex] = useState("");

  const mappedProcesses = element?.processes?.map((process, index) => ({
    ...process,
    processName: process?.processName || "",
    airEmissions: process?.airEmissions || [],
    emissions: (process?.emissions || []).map((emission) => ({
      emissionsName: emission?.name || emission?.emissionsName || "",
      unit: emission?.unit || "",
      quantity: emission?.quantity || "",
    })),
  })) || [
    {
      processName: "",
      airEmissions: [],
      emissions: [{ emissionsName: "", unit: "", quantity: "" }],
    },
  ];

  const [availableMethods, setAvailableMethods] = useState([]);
  const disposalMethodDropdownOptions =
    formData?.disposalMethod &&
    !availableMethods.some((m) => m?.type === formData.disposalMethod)
      ? [
          { type: formData.disposalMethod, values: formData?.values || {} },
          ...availableMethods,
        ]
      : availableMethods;

  const isProductDetailsSectionErrorKey = (key) => {
    const productKeys = new Set([
      "category",
      "description",
      "soldUnit",
      "weight",
      "usePhaseEnergyConsumption",
      "usePhaseEnergyConsumptionUnit",
      "energyUsedForMaintenance",
      "energyUsedForMaintenanceUnit",
      "frequency",
      "lifetime",
      "disposalMethod",
      "fuelEntries",
      "usePhaseFuelEntries",
    ]);
    if (productKeys.has(key)) return true;
    if (key.startsWith("fuelEntries.")) return true;
    if (key.startsWith("usePhaseFuelEntries.")) return true;
    return false;
  };

  const handleSkipManufacturingProcess = (e) => {
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      skipManufacturingProcess: checked,
      skipManufacturingProcessReason: checked
        ? prev.skipManufacturingProcessReason || ""
        : "",
      processes: checked
        ? []
        : [
            {
              processName: "",
              airEmissions: [],
              emissions: [{ emissionsName: "", unit: "", quantity: "" }],
            },
          ],
    }));

    if (checked) {
      setProcessEmission([]);
      setTable([]);

      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };

        Object.keys(newErrors).forEach((key) => {
          if (
            key.startsWith("processes") ||
            key.startsWith("airEmissions") ||
            key.startsWith("emissionsName") ||
            key.startsWith("quantity_") ||
            key.startsWith("unit_") ||
            key === "emissionManufacturingSection" ||
            key === "skipManufacturingProcessReason"
          ) {
            delete newErrors[key];
          }
        });

        return applySectionSummaryErrors(newErrors);
      });
    } else {
      const emptyRow = {
        processName: "",
        airEmissions: [],
        emissions: [{ emissionsName: "", unit: "", quantity: "" }],
      };

      setProcessEmission([emptyRow]);
      setTable([
        {
          index: 0,
          processName: "",
          airEmissions: [],
          emissions: [{ emissionsName: "", unit: "", quantity: "" }],
          colspan: 1,
        },
      ]);
    }
  };

  const isEmissionManufacturingSectionErrorKey = (key) => {
    if (key === "productName" || key === "productId") return false;
    if (isProductDetailsSectionErrorKey(key)) return false;
    return true;
  };

  /** When accordions are collapsed, surface section errors above each accordion. */
  const applySectionSummaryErrors = (rawErrors) => {
    const next = { ...rawErrors };
    delete next.productDetailsSection;
    delete next.emissionManufacturingSection;

    const activeKeys = Object.entries(next)
      .filter(([, v]) => typeof v === "string" && String(v).trim() !== "")
      .map(([k]) => k);

    if (activeKeys.some(isProductDetailsSectionErrorKey)) {
      next.productDetailsSection = "Please fill product details form";
    }
    // if (activeKeys.some(isEmissionManufacturingSectionErrorKey)) {
    //   next.emissionManufacturingSection =
    //     "Please fill emission from manufacturing process form";
    // }
    if (
      !formData?.skipManufacturingProcess &&
      activeKeys.some(isEmissionManufacturingSectionErrorKey)
    ) {
      next.emissionManufacturingSection =
        "Please fill emission from manufacturing process form";
    }
    return next;
  };

  const setErrorsWithSections = (updater) => {
    setErrors((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return applySectionSummaryErrors(next);
    });
  };

  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;

    const found = productCategoryOptions.find(
      (item) => item.category === selectedCategory,
    );

    setAvailableMethods(found ? found.methods : []);

    setFormData({
      ...formData,
      category: selectedCategory,
      disposalMethod: "",
      values: {},
    });
    setErrors((prev) =>
      applySectionSummaryErrors({
        ...prev,
        category: "",
      }),
    );
  };

  const handleMethodChange = (e) => {
    const method = e.target.value;

    const selected = availableMethods.find((m) => m.type === method);

    setFormData({
      ...formData,
      disposalMethod: method,
      values: selected?.values || {},
    });
    setErrors((prev) =>
      applySectionSummaryErrors({
        ...prev,
        disposalMethod: "",
      }),
    );
  };

  const resetForm = () => {
    if (id && element) {
      // Existing edit mode logic
      setFormData({
        ...initialState,
        processes: mappedProcesses,
      });
      setFormData2({
        ...initialState,
        processes: mappedProcesses,
      });
      setTable(
        mappedProcesses.map((process, index) => ({
          index,
          processName: process.processName,
          airEmissions: process.airEmissions,
          emissions: process.emissions,
          colspan: process.emissions.length || 1,
        })),
      );
      setProcessEmission(mappedProcesses);
    } else {
      // Add mode - initialize with one empty row
      const emptyRow = {
        processName: "",
        airEmissions: [],
        emissions: [{ emissionsName: "", unit: "", quantity: "" }],
      };

      setFormData({
        ...initialState,
        processes: [emptyRow],
      });
      setFormData2({
        ...initialState,
        processes: [emptyRow],
      });
      setTable([
        {
          index: 0,
          processName: "",
          airEmissions: [],
          emissions: [{ emissionsName: "", unit: "", quantity: "" }],
          colspan: 1,
        },
      ]);
      setProcessEmission([emptyRow]);
    }
  };

  const resetFormRef = useRef(resetForm);
  resetFormRef.current = resetForm;

  /** Layout effect so form state matches `element` before other effects (e.g. sample basis → unit) run. */
  useLayoutEffect(() => {
    resetFormRef.current();
  }, [element, id]);

  useEffect(() => {
    const found = productCategoryOptions.find(
      (item) => item.category === formData?.category,
    );
    setAvailableMethods(found ? found.methods : []);
  }, [formData?.category]);

  useEffect(() => {
    const basisForUnit = String(formData?.sampleBasis ?? "").trim();
    if (basisForUnit) {
      const unitOptions = getSampleBasisUnitOptions(basisForUnit, simpleUnits);

      setUnits(unitOptions);
      setFormData((prev) => {
        const elementBasis =
          id && element ? String(element.sampleBasis ?? "").trim() : "";
        const savedFromRecord =
          id &&
          element &&
          elementBasis === basisForUnit &&
          element.unit != null &&
          String(element.unit).trim() !== ""
            ? String(element.unit)
            : "";
        const raw =
          (prev.unit != null && String(prev.unit).trim() !== ""
            ? String(prev.unit)
            : "") || savedFromRecord;
        return {
          ...prev,
          unit: resolveSampleUnitToOption(raw, unitOptions),
        };
      });
    } else {
      setUnits([]);
      setFormData((prev) => ({
        ...prev,
        unit: "",
      }));
    }
  }, [formData.sampleBasis, simpleUnits, id, element]);

  useEffect(() => {
    return () => {
      if (processTimeout) clearTimeout(processTimeout);
    };
  }, [processTimeout]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const [baseField, field, rawIndex] = name.split(".");

    // Update form data
    if (
      name === "sampleQuantity" ||
      name === "energyUsedForMaintenance" ||
      name === "usePhaseEnergyConsumption" ||
      name === "weight"
    ) {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }
    if (name === "frequency") {
      if (!/^\d*$/.test(value)) return;
    }
    if (baseField === "fuelEntries" && field === "quantity") {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }
    if (baseField === "usePhaseFuelEntries" && field === "quantity") {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }
    const index = parseInt(rawIndex, 10);

    setFormData((prevState) => {
      if (baseField === "processes") {
        return {
          ...prevState,
          [baseField]: prevState[baseField]?.map((item, idx) => {
            return idx === index ? { ...item, [field]: value } : item;
          }),
        };
      } else if (baseField === "fuelEntries") {
        const updatedFuelEntries = prevState.fuelEntries?.map((item, idx) => {
          if (idx !== index) return item;
          return {
            ...item,
            [field]: value,
            ...(field === "fuelName" ? { unit: "" } : {}),
          };
        });

        return {
          ...prevState,
          fuelEntries: updatedFuelEntries,
          fuelName: updatedFuelEntries?.[0]?.fuelName ?? "",
          quantity: updatedFuelEntries?.[0]?.quantity ?? "",
        };
      } else if (baseField === "usePhaseFuelEntries") {
        const updatedUsePhaseFuelEntries = prevState.usePhaseFuelEntries?.map(
          (item, idx) => {
            if (idx !== index) return item;
            return {
              ...item,
              [field]: value,
              ...(field === "fuelName" ? { unit: "" } : {}),
            };
          },
        );

        return {
          ...prevState,
          usePhaseFuelEntries: updatedUsePhaseFuelEntries,
        };
      } else {
        return {
          ...prevState,
          [name]: value,
        };
      }
    });

    // Clear errors for this field
    setErrors((prevState) =>
      applySectionSummaryErrors({
        ...prevState,
        [name]: "",
      }),
    );

    if (name === "productId") {
      const trimmed = value?.toString().trim();
      if (trimmed && Array.isArray(productsListForIdCheck)) {
        const dup = productsListForIdCheck.find(
          (el) =>
            el?.productId?.toString().trim().toLowerCase() ===
            trimmed.toLowerCase(),
        );
        if (dup && (!element?._id || element._id !== dup._id)) {
          setErrors((prev) =>
            applySectionSummaryErrors({
              ...prev,
              productId: "This ID already exists.",
            }),
          );
        }
      }
    }

    if (baseField === "processes") {
      if (processTimeout) clearTimeout(processTimeout);

      const newTimeout = setTimeout(async () => {
        if (value.trim() === "") return;

        setTable((prevTable) => {
          const updatedTable = [...prevTable];
          const rowIndex = updatedTable.findIndex((row) => row.index === index);

          if (rowIndex >= 0) {
            // Update existing row
            updatedTable[rowIndex].processName = value;
          } else {
            // This else block was creating new rows unnecessarily
            // We shouldn't need this since we're only updating existing rows
            console.warn("Trying to update non-existent row");
          }

          return updatedTable;
        });
      }, 600); // Debounce delay

      setProcessTimeout(newTimeout);
    }
  };

  const handleChangeEmission = async (name, value) => {
    const [baseField, field, rawIndex] = name.split(".");
    const index = parseInt(rawIndex, 10);

    setFormData((prevState) => {
      if (baseField === "processes") {
        return {
          ...prevState,
          [baseField]: prevState[baseField]?.map((item, idx) => {
            if (idx !== index) return item;
            if (field === "emissions") {
              const mappedEmissions = (value || []).map((val, idx) => {
                const previous = item.emissions?.[idx] || {};
                return {
                  emissionsName:
                    typeof val === "object"
                      ? val.emissionsName || val.name || val
                      : val,
                  unit: previous.unit || "",
                  quantity: previous.quantity || "",
                };
              });

              return {
                ...item,
                airEmissions: value || [],
                emissions: [...mappedEmissions],
              };
            }

            return {
              ...item,
              [field]: value,
            };
          }),
        };
      } else {
        return {
          ...prevState,
          [name]: value,
        };
      }
    });
    console.log(baseField, field, index, "baseField", errors);
    if (baseField === "processes") {
      setTable((prevTable) => {
        const updatedTable = [...prevTable];
        const emissions = value || [];
        console.log(emissions, "formData[emissionKey]");
        const rowIndex = updatedTable.findIndex((row) => row.index === index);

        if (rowIndex >= 0) {
          updatedTable[rowIndex].emissions = emissions.reduce(
            (acc, item, idx) => {
              if (!Array.isArray(acc)) acc = [];
              acc.push({
                emissionsName: item.name || item, // Fallback if item is a string
                quantity: item.quantity || "",
                unit: item.unit || "",
              });
              return acc;
            },
            [],
          );
          updatedTable[rowIndex].colspan = emissions.length || 1;
        }

        return updatedTable;
      });
    }
    setErrors((prevState) =>
      applySectionSummaryErrors({
        ...prevState,
        [`airEmissions.${index}`]: "",
      }),
    );
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setAddData(false);
    setErrors({});
    console.log(initialState, "initial");
    resetForm();
  };

  const inputTableData = [
    {
      title: "Process Name",
      colwidth: "28%",
      filedName: "processes",
      placeholder: "Process Name",
    },
    {
      title: "Emission",
      field: "emissions",
      placeholder: "Emission",
    },
    {
      title: "Quantity",
      field: "quantity",
      type: "number",
      placeholder: "Enter Quantity",
    },
    {
      title: "Unit",
      field: "unit",
      type: "select",
      placeholder: "Unit",
    },
  ];

  const handleClose = () => {
    const isFormDataUnchanged = Object.keys(formData).every((key) => {
      if (key === "processes") {
        // Deep compare the processes array
        return (
          JSON.stringify(formData.processes) ===
          JSON.stringify(formData2.processes)
        );
      } else {
        // Simple compare for primitive fields
        return formData[key] === formData2[key];
      }
    });

    console.log(isFormDataUnchanged, formData, formData2, "key1");

    if (isFormDataUnchanged) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateForm = () => {
    const newErrors = {};

  const requiredFields = [
    "productName",
    "productId",
    "category",
    "description",
    "soldUnit",
    "weight",
    "energyUsedForMaintenance",
    "energyUsedForMaintenanceUnit",
    "usePhaseEnergyConsumption",
    "usePhaseEnergyConsumptionUnit",
    "frequency",
    "disposalMethod",
    "lifetime",
    ...(formData.skipManufacturingProcess ? [] : ["sampleQuantity", "unit"]),
  ];

    requiredFields.forEach((field) => {
      const value = formData[field];
      if (value === null || value === undefined || value.toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    if (
      formData.skipManufacturingProcess &&
      !formData.skipManufacturingProcessReason?.toString().trim()
    ) {
      newErrors.skipManufacturingProcessReason =
        "Please enter reason for skipping manufacturing process.";
    }

    if (formData.frequency && !/^\d+$/.test(String(formData.frequency))) {
      newErrors.frequency = "Only whole numbers are allowed.";
    }

    const productIdTrimmed = formData.productId?.toString().trim();
    if (productIdTrimmed && Array.isArray(productsListForIdCheck)) {
      const dup = productsListForIdCheck.find(
        (el) =>
          el?.productId?.toString().trim().toLowerCase() ===
          productIdTrimmed.toLowerCase(),
      );
      if (dup && (!element?._id || element._id !== dup._id)) {
        newErrors.productId = "This ID already exists.";
      }
    }
    if (!formData.skipManufacturingProcess) {
      inputData.forEach((input) => {
        // If input itself is an array, loop through each item in it
        if (Array.isArray(input)) {
          input.forEach((fields) => {
            const value =
              formData[
                fields.fieldName.includes(".")
                  ? fields.fieldName.split(".")[0]
                  : fields.fieldName
              ];
            if (Array.isArray(value)) {
              value.forEach((item, index) => {
                if (fields.fieldName.includes("processes")) {
                  if (!item.processName || item.processName.trim() === "") {
                    newErrors[`processes.processName.${index}`] =
                      "Process name is required.";
                  }

                  // validate airEmissions if needed
                  if (!item.airEmissions || item.airEmissions.length === 0) {
                    newErrors[`airEmissions.${index}`] =
                      "At least one emission type is required.";
                  }

                  if (
                    item?.processName !== "" &&
                    item?.airEmissions?.length > 0
                  ) {
                    item.emissions.forEach((emission, i) => {
                      if (!emission.emissionsName) {
                        newErrors[`emissionsName_${index}_${i}`] =
                          "Emission name is required.";
                      }
                      if (!emission.unit) {
                        newErrors[`unit_${index}_${i}`] = "Unit is required.";
                      }
                      if (!emission.quantity) {
                        newErrors[`quantity_${index}_${i}`] =
                          "Quantity is required.";
                      }
                    });
                  }
                }
              });
            } else if (!value || value === "") {
              newErrors[fields.fieldName] = "This field is required.";
            }
          });
        } else {
          // If the field is an array in formData (like processes), validate accordingly
          const value = formData[input.fieldName];

          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              if (input.fieldName.includes("processes")) {
                if (!item.processName || item.processName.trim() === "") {
                  newErrors[`processes.processName_${index}`] =
                    "Process name is required.";
                }

                // validate airEmissions if needed
                if (!item.airEmissions || item.airEmissions.length === 0) {
                  newErrors[`airEmissions_${index}`] =
                    "At least one emission type is required.";
                }

                item.emissions.forEach((emission, i) => {
                  if (!emission.emissionsName) {
                    newErrors[`emissionsName_${index}_${i}`] =
                      "Emission name is required.";
                  }
                  if (!emission.unit) {
                    newErrors[`unit_${index}_${i}`] = "Unit is required.";
                  }
                  if (!emission.quantity) {
                    newErrors[`quantity_${index}_${i}`] =
                      "Quantity is required.";
                  }
                });
              }
            });
          }
        }
      });
    }

    // setErrors({ ...errors, ...newErrors });
    if (
      !Array.isArray(formData.fuelEntries) ||
      formData.fuelEntries.length === 0
    ) {
      newErrors["fuelEntries"] = "At least one fuel entry is required.";
    } else {
      formData.fuelEntries.forEach((entry, idx) => {
        if (!entry?.fuelName?.toString().trim()) {
          newErrors[`fuelEntries.fuelName.${idx}`] = "Fuel name is required.";
        }
        if (!entry?.quantity?.toString().trim()) {
          newErrors[`fuelEntries.quantity.${idx}`] = "Quantity is required.";
        }
        if (!entry?.unit?.toString().trim()) {
          newErrors[`fuelEntries.unit.${idx}`] = "Unit is required.";
        }
      });
      const selectedMaintenanceFuelNames = formData.fuelEntries
        .map((entry) => entry?.fuelName?.toString().trim())
        .filter(Boolean);
      if (
        new Set(selectedMaintenanceFuelNames).size !==
        selectedMaintenanceFuelNames.length
      ) {
        newErrors["fuelEntries"] =
          "Fuel names in maintenance must be different.";
      }
    }

    if (
      !Array.isArray(formData.usePhaseFuelEntries) ||
      formData.usePhaseFuelEntries.length === 0
    ) {
      newErrors["usePhaseFuelEntries"] = "At least one fuel entry is required.";
    } else {
      formData.usePhaseFuelEntries.forEach((entry, idx) => {
        if (!entry?.fuelName?.toString().trim()) {
          newErrors[`usePhaseFuelEntries.fuelName.${idx}`] =
            "Fuel name is required.";
        }
        if (!entry?.quantity?.toString().trim()) {
          newErrors[`usePhaseFuelEntries.quantity.${idx}`] =
            "Quantity is required.";
        }
        if (!entry?.unit?.toString().trim()) {
          newErrors[`usePhaseFuelEntries.unit.${idx}`] = "Unit is required.";
        }
      });
      const selectedUsePhaseFuelNames = formData.usePhaseFuelEntries
        .map((entry) => entry?.fuelName?.toString().trim())
        .filter(Boolean);
      if (
        new Set(selectedUsePhaseFuelNames).size !==
        selectedUsePhaseFuelNames.length
      ) {
        newErrors["usePhaseFuelEntries"] =
          "Fuel names in use phase must be different.";
      }
    }

    const merged = applySectionSummaryErrors(newErrors);
    setErrors(merged);
    return !Object.values(merged).some(
      (v) => typeof v === "string" && String(v).trim() !== "",
    );
  };

  const Save = async () => {
    console.log(validateForm(), "validateForm()for the validation");
    if (!validateForm()) return;
    const payload = {
      ...formData,
      soldUnitQuantity: "",
      processes: formData.skipManufacturingProcess ? [] : formData.processes,
      energyUsedForMaintenanceUnit:
        formData.energyUsedForMaintenanceUnit || "Mwh",
      usePhaseEnergyConsumptionUnit:
        formData.usePhaseEnergyConsumptionUnit || "Mwh",
    };
    console.log(formData, "checking formData");
    let method = id ? put : post;
    let url = id ? `${updateUrl}/${id}` : addUrl;

    await method(url, payload)
      .then((res) => {
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        fetchData();
        handleExitAgree();
      })
      .catch((err) => {
        const apiMessage = err?.response?.data?.message;
        if (apiMessage === "Product already exists") {
          setErrors((prev) =>
            applySectionSummaryErrors({
              ...prev,
              productName: "Product already exists.",
            }),
          );
          return;
        }
        const duplicateProductId =
          typeof apiMessage === "string" &&
          (/product id already exists/i.test(apiMessage) ||
            /productid already exists/i.test(apiMessage) ||
            /duplicate.*product.*id/i.test(apiMessage));
        if (duplicateProductId) {
          setErrors((prev) =>
            applySectionSummaryErrors({
              ...prev,
              productId: "This ID already exists.",
            }),
          );
          return;
        }
        console.log(apiMessage, "err1");
        if (err.response && err.response.data && err.response.data.errors) {
          const errorMessages = err.response.data.errors
            .map((error) => error?.msg)
            .filter((msg) => typeof msg === "string" && msg.trim() !== "")
            .join(", ");
          setsnackOpen({
            open: true,
            message:
              errorMessages ||
              (typeof apiMessage === "string" && apiMessage.trim()
                ? apiMessage
                : "Please fill all required fields correctly."),
            severity: "error",
          });
        } else {
          const normalizedApiMessage =
            typeof apiMessage === "string"
              ? apiMessage.replace(/,\s*/g, ", ").trim()
              : "";
          setsnackOpen({
            open: true,
            message:
              normalizedApiMessage ||
              "Please fill all required fields correctly.",
            severity: "error",
          });
        }
      });
  };

  const isFilled = () => {
    let data = formData?.processes?.[formData?.processes?.length - 1];
    console.log(formData, data, "formData");
    if (data?.processName !== "" && data?.airEmissions?.length !== 0)
      return false;
    return true;
  };

  const handleAdd = () => {
    const newItem = {
      processName: "",
      airEmissions: [],
      emissions: [{ emissionsName: "", unit: "", quantity: "" }],
    };

    setProcessEmission((prev) => [...prev, newItem]);
    setFormData((prevState) => ({
      ...prevState,
      processes: [...prevState.processes, newItem],
    }));

    // Also update the table state
    setTable((prev) => [
      ...prev,
      {
        index: prev.length,
        processName: "",
        airEmissions: [],
        emissions: [{ emissionsName: "", unit: "", quantity: "" }],
        colspan: 1,
      },
    ]);
  };

  const handleDelete = (index) => {
    if (formData.processes?.length <= 1) {
      return;
    }
    const process = formData?.processes?.[index];
    console.log(process, "process");
    if (process?.airEmissions?.length === 0 && process?.processName === "") {
      setFormData((prevState) => ({
        ...prevState,
        processes: prevState.processes?.filter((_, i) => i !== index), // Simplified filter
      }));

      setProcessEmission((prev) => prev.filter((_, i) => i !== index)); // Simplified filter
      setTable((prev) => prev.filter((_, i) => i !== index)); // Simplified filter
      setOpenDialog(false);
      setRemoveIndex("");
      setErrors((prev) => applySectionSummaryErrors({ ...prev }));
    } else {
      setRemoveIndex(index);

      setOpenDialog(true);
    }
  };

  const handleAddFuelEntry = () => {
    if ((formData.fuelEntries || []).length >= 2) return;
    setFormData((prevState) => ({
      ...prevState,
      fuelEntries: [
        ...(prevState.fuelEntries || []),
        { fuelName: "", quantity: "", unit: "" },
      ],
    }));
  };

  const handleRemoveFuelEntry = (index) => {
    setFormData((prevState) => {
      const nextFuelEntries = (prevState.fuelEntries || []).filter(
        (_, idx) => idx !== index,
      );
      const safeFuelEntries =
        nextFuelEntries.length > 0
          ? nextFuelEntries
          : [{ fuelName: "", quantity: "", unit: "" }];

      return {
        ...prevState,
        fuelEntries: safeFuelEntries,
        fuelName: safeFuelEntries[0]?.fuelName ?? "",
        quantity: safeFuelEntries[0]?.quantity ?? "",
      };
    });
  };

  const handleAddUsePhaseFuelEntry = () => {
    if ((formData.usePhaseFuelEntries || []).length >= 2) return;
    setFormData((prevState) => ({
      ...prevState,
      usePhaseFuelEntries: [
        ...(prevState.usePhaseFuelEntries || []),
        { fuelName: "", quantity: "", unit: "" },
      ],
    }));
  };

  const handleRemoveUsePhaseFuelEntry = (index) => {
    setFormData((prevState) => {
      const nextUsePhaseFuelEntries = (
        prevState.usePhaseFuelEntries || []
      ).filter((_, idx) => idx !== index);
      return {
        ...prevState,
        usePhaseFuelEntries:
          nextUsePhaseFuelEntries.length > 0
            ? nextUsePhaseFuelEntries
            : [{ fuelName: "", quantity: "", unit: "" }],
      };
    });
  };

  const isFuelEntryComplete = (entry) =>
    !!entry &&
    entry.fuelName?.toString().trim() !== "" &&
    entry.quantity?.toString().trim() !== "" &&
    entry.unit?.toString().trim() !== "";

  const getSelectedFuelNames = (entries = [], currentFuelIndex = -1) =>
    new Set(
      (Array.isArray(entries) ? entries : [])
        .filter((_, idx) => idx !== currentFuelIndex)
        .map((entry) => entry?.fuelName?.toString().trim())
        .filter(Boolean),
    );

  const isAddFuelDisabled =
    !Array.isArray(formData.fuelEntries) ||
    formData.fuelEntries.length === 0 ||
    !formData.fuelEntries.every((entry) => isFuelEntryComplete(entry)) ||
    formData.fuelEntries.length >= 2;

  const isAddUsePhaseFuelDisabled =
    !Array.isArray(formData.usePhaseFuelEntries) ||
    formData.usePhaseFuelEntries.length === 0 ||
    !formData.usePhaseFuelEntries.every((entry) =>
      isFuelEntryComplete(entry),
    ) ||
    formData.usePhaseFuelEntries.length >= 2;

  const cancelRemoveRow = () => {
    setOpenDialog(false);
  };

  const confirmRemoveRow = () => {
    setFormData((prevState) => ({
      ...prevState,
      processes: prevState.processes?.filter((_, i) => i !== removeIndex), // Simplified filter
    }));

    setProcessEmission((prev) => prev.filter((_, i) => i !== removeIndex)); // Simplified filter
    setTable((prev) => prev.filter((_, i) => i !== removeIndex)); // Simplified filter
    setOpenDialog(false);
    setRemoveIndex("");
    setErrors((prev) => applySectionSummaryErrors({ ...prev }));
  };

  const renderInputField = (el) => {
    console.log("datael", el);
    if (Array.isArray(el)) {
      const hasHeader = el.some((item) => item.header);
      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "row",
            gap: "8px",
            width: "100%",
          }}
        >
          {hasHeader && (
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", width: "100%", mb: 1 }}
            >
              {el.find((item) => item.header)?.header}
            </Typography>
          )}
          {el.map((item) => {
            return (
              <Box sx={{ width: item.colwidth || "100%" }}>
                {item.type === "select" ? (
                  <SelectField
                    title={item.title}
                    name={item.fieldName}
                    value={formData[item.fieldName]}
                    //   handleChange={handleCountryChange}
                    handleChange={handleChange}
                    options={item.options}
                    errors={errors[item.fieldName]}
                  />
                ) : item.fieldName === "unit" ? (
                  <SelectField
                    title={item.title}
                    name={item.fieldName}
                    value={formData[item?.fieldName] ?? ""}
                    defaultValue="Tap to Select"
                    //   handleChange={handleCountryChange}
                    handleChange={handleChange}
                    options={units || []}
                    errors={errors[item.fieldName]}
                  />
                ) : (
                  <InputField
                    title={item.title}
                    handleChange={handleChange}
                    placeholder={item.placeholder}
                    value={
                      formData[item.fieldName] === undefined ||
                      formData[item.fieldName] === null
                        ? ""
                        : formData[item.fieldName]
                    }
                    errors={errors[item.fieldName]}
                    fieldName={item.fieldName}
                    inputSx={
                      item.fieldName === "sampleQuantity"
                        ? {
                            "& .MuiInputBase-root": {
                              paddingTop: "0px",
                              paddingBottom: "0px",
                              paddingLeft: "0.75rem",
                            },
                          }
                        : undefined
                    }
                  />
                )}
              </Box>
            );
          })}
        </Box>
      );
    }

    if (el.header) {
      return (
        <Typography
          // key={idx}
          variant="subtitle1"
          sx={{ fontWeight: "bold", width: "100%", mt: 2 }}
        >
          {el.header}
        </Typography>
      );
    }
  };

  const list = () => (
    <Box display="flex" gap={3} sx={{ width: "40.6rem" }}>
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      <DeleteRowDialog {...{ openDialog, cancelRemoveRow, confirmRemoveRow }} />
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "20px",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "3%",
        }}
      >
        <IconButton onClick={handleClose} sx={{ color: "black" }}>
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "100%",
          pl: 3,
          pr: 1,
          py: "1.5rem",
          backgroundColor: "#fff",
          height: "100vh",
          overflowY: "scroll",
          "& .MuiTextField-root .MuiOutlinedInput-root": {
            borderRadius: "6px",
            minHeight: "40px",
          },
          "& .MuiTextField-root .MuiInputBase-input": {
            fontSize: "16px",
            paddingTop: "9px",
            paddingBottom: "9px",
          },
          "& .MuiTextField-root .MuiSelect-select": {
            paddingTop: "9px",
            paddingBottom: "9px",
          },
          "& .MuiTypography-subtitle1": {
            marginBottom: "2px",
            lineHeight: 1.25,
          },
          "& .MuiFormHelperText-root": {
            marginLeft: 0,
            marginRight: 0,
            marginTop: "4px",
            fontSize: "14px",
            lineHeight: 1.35,
            textAlign: "left",
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
        >
          {id ? "Edit Product" : "Add Product"}
        </Typography>

        <Box display="flex" flexWrap="wrap">
          {/* Basic Fields */}
          <Box display="flex" flexWrap="wrap" gap={1.25}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", width: "100%", mb: 0.3 }}
            >
              Product Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter product name"
              // label="Product Name"
              name="productName"
              value={formData.productName}
              onChange={handleChange}
              error={!!errors.productName}
              helperText={errors.productName || ""}
            />
            {/* 
              <InputField
                title="Product Name"
                handleChange={handleChange}
                placeholder="Enter product name"
                value={formData.productName || ""}
                errors={errors.productName}
                fieldName="productName"
              /> */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
            >
              Product ID.
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter product ID"
              name="productId"
              value={formData.productId}
              onChange={handleChange}
              error={!!errors.productId}
              helperText={errors.productId || ""}
            />
          </Box>

          {/* Accordion - Product Details */}
          <Box sx={{ width: "100%", mt: 2 }}>
            {errors.productDetailsSection ? (
              <Typography
                component="div"
                role="alert"
                sx={(theme) => ({
                  mb: 1,
                  ml: 0,
                  mt: "4px",
                  fontSize: "14px",
                  lineHeight: 1.35,
                  fontWeight: 400,
                  color: theme.palette.error.main,
                  fontFamily: theme.typography.fontFamily,
                })}
              >
                {errors.productDetailsSection}
              </Typography>
            ) : null}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 16,
                    right: 16,
                    bottom: 0,
                    borderBottom: "1px solid #c4c4c4",
                  },
                }}
              >
                <Typography fontWeight="bold">Product Details</Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1.25}
                  sx={{ marginTop: "10px" }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Product Category (Material of product)
                  </Typography>
                  <TextField
                    select
                    placeholder="Select Material"
                    name="category"
                    value={formData.category}
                    onChange={handleCategoryChange}
                    error={!!errors.category}
                    helperText={errors.category || ""}
                    fullWidth
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) =>
                        selected && selected !== ""
                          ? selected
                          : "Select Material",
                    }}
                  >
                    <MenuItem value="">Select Material</MenuItem>

                    {productCategoryOptions.map((item, index) => (
                      <MenuItem key={index} value={item.category}>
                        {item.category}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Product Description
                  </Typography>
                  <TextField
                    placeholder="Enter product description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    error={!!errors.description}
                    helperText={errors.description || ""}
                  />

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Sold unit
                  </Typography>
                  <TextField
                    // required
                    select
                    name="soldUnit"
                    label="Unit"
                    placeholder="Select Unit"
                    value={formData.soldUnit || ""}
                    onChange={handleChange}
                    error={!!errors.soldUnit}
                    helperText={errors.soldUnit || ""}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) =>
                        selected && selected !== "" ? selected : "Select Unit",
                    }}
                  >
                    <MenuItem value="">Select Unit</MenuItem>
                    {SOLD_UNIT_OPTIONS.map((u) => (
                      <MenuItem key={u} value={u}>
                        {u}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Average weight per unit of product
                  </Typography>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    {/* <Typography variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 0.3 }}>
                    Average weight per unit of product
                  </Typography> */}
                    <TextField
                      placeholder="Average weight per unit of product"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      inputProps={{ inputMode: "decimal" }}
                      error={!!errors.weight}
                      helperText={errors.weight || ""}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Unit"
                      value="MT"
                      disabled
                      sx={{ flex: 0.35 }}
                    />
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Average annual electricity consumption in use phase of product
                  </Typography>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <TextField
                      placeholder="Average annual electricity consumption in use phase"
                      name="usePhaseEnergyConsumption"
                      value={formData.usePhaseEnergyConsumption}
                      onChange={handleChange}
                      error={!!errors.usePhaseEnergyConsumption}
                      helperText={errors.usePhaseEnergyConsumption || ""}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Unit"
                      value="Mwh"
                      disabled
                      error={!!errors.usePhaseEnergyConsumptionUnit}
                      helperText={errors.usePhaseEnergyConsumptionUnit || ""}
                      sx={{ flex: 0.35 }}
                    />
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Fuel required for use (You can select upto two fuel type)
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        flex: 1.3,
                        minWidth: 180,
                        mb: 0.1,
                      }}
                    >
                      Fuel Name
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        flex: 0.8,
                        minWidth: 120,
                        mb: 0.1,
                      }}
                    >
                      Quantity
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        flex: 0.8,
                        minWidth: 150,
                        mb: 0.1,
                      }}
                    >
                      Unit
                    </Typography>
                  </Box>
                  {(formData.usePhaseFuelEntries || []).map(
                    (fuel, fuelIndex) => (
                      <Box
                        display="flex"
                        gap={1}
                        key={`use-phase-fuel-row-${fuelIndex}`}
                      >
                        <TextField
                          select
                          placeholder="Fuel Name"
                          name={`usePhaseFuelEntries.fuelName.${fuelIndex}`}
                          value={fuel.fuelName}
                          onChange={handleChange}
                          sx={{ flex: 1.3, minWidth: 180 }}
                          error={
                            !!errors[
                              `usePhaseFuelEntries.fuelName.${fuelIndex}`
                            ]
                          }
                          helperText={
                            errors[
                              `usePhaseFuelEntries.fuelName.${fuelIndex}`
                            ] || ""
                          }
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (selected) =>
                              selected && selected !== ""
                                ? selected
                                : "Tap to select",
                          }}
                        >
                          <MenuItem value="">Select fuel type</MenuItem>
                          {energyTypeOptions.map((fuelType) => (
                            <MenuItem
                              key={fuelType}
                              value={fuelType}
                              disabled={getSelectedFuelNames(
                                formData.usePhaseFuelEntries || [],
                                fuelIndex,
                              ).has(fuelType)}
                            >
                              {fuelType}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          placeholder="Quantity"
                          name={`usePhaseFuelEntries.quantity.${fuelIndex}`}
                          value={fuel.quantity}
                          onChange={handleChange}
                          inputProps={{ inputMode: "decimal" }}
                          sx={{ flex: 0.8, minWidth: 120 }}
                          error={
                            !!errors[
                              `usePhaseFuelEntries.quantity.${fuelIndex}`
                            ]
                          }
                          helperText={
                            errors[
                              `usePhaseFuelEntries.quantity.${fuelIndex}`
                            ] || ""
                          }
                        />
                        <TextField
                          select
                          placeholder="Unit"
                          name={`usePhaseFuelEntries.unit.${fuelIndex}`}
                          value={fuel.unit}
                          onChange={handleChange}
                          sx={{ flex: 0.8, minWidth: 150 }}
                          error={
                            !!errors[`usePhaseFuelEntries.unit.${fuelIndex}`]
                          }
                          helperText={
                            errors[`usePhaseFuelEntries.unit.${fuelIndex}`] ||
                            ""
                          }
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (selected) =>
                              selected && selected !== ""
                                ? selected
                                : "Tap to Select",
                          }}
                        >
                          <MenuItem value="">Tap to Select</MenuItem>
                          {(FUEL_UNIT_BY_TYPE[fuel.fuelName] || []).map((u) => (
                            <MenuItem key={u} value={u}>
                              {u}
                            </MenuItem>
                          ))}
                        </TextField>
                        {(formData.usePhaseFuelEntries || []).length > 1 && (
                          <IconButton
                            color="error"
                            onClick={() =>
                              handleRemoveUsePhaseFuelEntry(fuelIndex)
                            }
                            sx={{ alignSelf: "center" }}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    ),
                  )}
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handleAddUsePhaseFuelEntry}
                      disabled={isAddUsePhaseFuelDisabled}
                      sx={{ width: "fit-content" }}
                    >
                      <AddIcon sx={{ mr: 0.5 }} />
                      Add new
                    </Button>
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Electricity Used for Maintenance
                  </Typography>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <TextField
                      placeholder="Energy Used for Maintenance"
                      name="energyUsedForMaintenance"
                      value={formData.energyUsedForMaintenance}
                      onChange={handleChange}
                      error={!!errors.energyUsedForMaintenance}
                      helperText={errors.energyUsedForMaintenance || ""}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Unit"
                      value="Mwh"
                      disabled
                      error={!!errors.energyUsedForMaintenanceUnit}
                      helperText={errors.energyUsedForMaintenanceUnit || ""}
                      sx={{ flex: 0.35 }}
                    />
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Fuel Used for Maintenance (You can select upto two fuel
                    type)
                  </Typography>

                  <Box display="flex" gap={1}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        flex: 1.3,
                        minWidth: 180,
                        mb: 0.1,
                      }}
                    >
                      Fuel Name
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        flex: 0.8,
                        minWidth: 120,
                        mb: 0.1,
                      }}
                    >
                      Quantity
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        flex: 0.8,
                        minWidth: 150,
                        mb: 0.1,
                      }}
                    >
                      Unit
                    </Typography>
                  </Box>
                  {(formData.fuelEntries || []).map((fuel, fuelIndex) => (
                    <Box display="flex" gap={1} key={`fuel-row-${fuelIndex}`}>
                      <TextField
                        select
                        placeholder="Fuel Name"
                        name={`fuelEntries.fuelName.${fuelIndex}`}
                        value={fuel.fuelName}
                        onChange={handleChange}
                        sx={{ flex: 1.3, minWidth: 180 }}
                        error={!!errors[`fuelEntries.fuelName.${fuelIndex}`]}
                        helperText={
                          errors[`fuelEntries.fuelName.${fuelIndex}`] || ""
                        }
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) =>
                            selected && selected !== ""
                              ? selected
                              : "Tap to select",
                        }}
                      >
                        <MenuItem value="">Select fuel type</MenuItem>
                        {energyTypeOptions.map((fuelType) => (
                          <MenuItem
                            key={fuelType}
                            value={fuelType}
                            disabled={getSelectedFuelNames(
                              formData.fuelEntries || [],
                              fuelIndex,
                            ).has(fuelType)}
                          >
                            {fuelType}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        placeholder="Quantity"
                        name={`fuelEntries.quantity.${fuelIndex}`}
                        value={fuel.quantity}
                        onChange={handleChange}
                        inputProps={{ inputMode: "decimal" }}
                        sx={{ flex: 0.8, minWidth: 120 }}
                        error={!!errors[`fuelEntries.quantity.${fuelIndex}`]}
                        helperText={
                          errors[`fuelEntries.quantity.${fuelIndex}`] || ""
                        }
                      />
                      <TextField
                        select
                        placeholder="Unit"
                        name={`fuelEntries.unit.${fuelIndex}`}
                        value={fuel.unit}
                        onChange={handleChange}
                        sx={{ flex: 0.8, minWidth: 150 }}
                        error={!!errors[`fuelEntries.unit.${fuelIndex}`]}
                        helperText={
                          errors[`fuelEntries.unit.${fuelIndex}`] || ""
                        }
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) =>
                            selected && selected !== ""
                              ? selected
                              : "Tap to Select",
                        }}
                      >
                        <MenuItem value="">Tap to Select</MenuItem>
                        {(FUEL_UNIT_BY_TYPE[fuel.fuelName] || []).map((u) => (
                          <MenuItem key={u} value={u}>
                            {u}
                          </MenuItem>
                        ))}
                      </TextField>
                      {(formData.fuelEntries || []).length > 1 && (
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveFuelEntry(fuelIndex)}
                          sx={{ alignSelf: "center" }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handleAddFuelEntry}
                      disabled={isAddFuelDisabled}
                      sx={{ width: "fit-content" }}
                    >
                      <AddIcon sx={{ mr: 0.5 }} />
                      Add new
                    </Button>
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Frequency of Maintenance per year
                  </Typography>

                  <TextField
                    placeholder="Maintenance Frequency"
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleChange}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={!!errors.frequency}
                    helperText={errors.frequency || ""}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Expected Lifetime of Product (years)
                  </Typography>
                  <TextField
                    placeholder="Expected Lifetime of Product (years)"
                    name="lifetime"
                    value={formData.lifetime}
                    onChange={(e) => {
                      const { value } = e.target;
                      if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
                      handleChange(e);
                    }}
                    inputProps={{ inputMode: "decimal" }}
                    error={!!errors.lifetime}
                    helperText={errors.lifetime || ""}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                  >
                    Disposal Method
                  </Typography>
                  <TextField
                    select
                    placeholder="Tap to select disposal method"
                    name="disposalMethod"
                    value={formData.disposalMethod}
                    onChange={handleMethodChange}
                    disabled={!formData.category}
                    error={!!errors.disposalMethod}
                    helperText={errors.disposalMethod || ""}
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        backgroundColor: "#f2f4f7",
                      },
                      "& .MuiInputBase-root.Mui-disabled .MuiSelect-select": {
                        color: "#9aa3af",
                        WebkitTextFillColor: "#9aa3af",
                      },
                      "& .MuiInputBase-root.Mui-disabled .MuiSvgIcon-root": {
                        color: "#9aa3af",
                      },
                      "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#d5d9e0",
                        },
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) =>
                        selected && selected !== ""
                          ? selected
                          : "Tap to select",
                    }}
                  >
                    <MenuItem value="">Tap to select</MenuItem>
                    {disposalMethodDropdownOptions.map((item, index) => (
                      <MenuItem key={index} value={item.type}>
                        {item.type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Another Section */}
          <Box sx={{ width: "100%", mt: 2 }}>
            {errors.emissionManufacturingSection ? (
              <Typography
                component="div"
                role="alert"
                sx={(theme) => ({
                  mb: 1,
                  ml: 0,
                  mt: "4px",
                  fontSize: "14px",
                  lineHeight: 1.35,
                  fontWeight: 400,
                  color: theme.palette.error.main,
                  fontFamily: theme.typography.fontFamily,
                })}
              >
                {errors.emissionManufacturingSection}
              </Typography>
            ) : null}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 16,
                    right: 16,
                    bottom: 0,
                    borderBottom: "1px solid #c4c4c4",
                  },
                }}
              >
                <Typography fontWeight="bold">
                  Emission from Manufacturing Process
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.skipManufacturingProcess}
                        onChange={handleSkipManufacturingProcess}
                        color="primary"
                      />
                    }
                    label={
                      formData.skipManufacturingProcess
                        ? "Skipped"
                        : "Skip Manufacturing Process"
                    }
                    labelPlacement="start"
                  />
                </Box>

                {formData.skipManufacturingProcess ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#777",
                        fontStyle: "italic",
                      }}
                    >
                      Skipping this data will impact the total GHG count under Scope 1 – Industrial Process Releases.
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", width: "100%", mb: 0.1 }}
                    >
                      Reason for Skip
                    </Typography>

                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Enter reason"
                      name="skipManufacturingProcessReason"
                      value={formData.skipManufacturingProcessReason || ""}
                      onChange={handleChange}
                      error={!!errors.skipManufacturingProcessReason}
                      helperText={errors.skipManufacturingProcessReason || ""}
                    />
                  </Box>
                ) : (
                  <>
                    {inputData
                      .slice(0, -1)
                      .map((el, index) => renderInputField(el))}

                    {processEmission.map((item, index) => {
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "8px",
                            width: "100%",
                            alignItems: "flex-start",
                            position: "relative",
                            mb: "-1.2rem",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              flexDirection: "row",
                              gap: "8px",
                              width: "calc(100% - 40px)",
                            }}
                          >
                            {inputData[3].map((item, idx) => (
                              <Box
                                key={idx}
                                sx={{ width: item.colwidth || "100%" }}
                              >
                                {item.fieldName === "processes.emissions" ? (
                                  <>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ fontWeight: "bold", mt: 1 }}
                                    >
                                      {item.title}
                                    </Typography>

                                    <Autocomplete
                                      multiple
                                      disablePortal
                                      size="small"
                                      displayEmpty
                                      options={typeofemissions}
                                      disabled={
                                        formData.processes[index]
                                          ?.processName === ""
                                      }
                                      value={
                                        formData?.processes[index]
                                          ?.airEmissions || []
                                      }
                                      onChange={(_, newValue) =>
                                        handleChangeEmission(
                                          `${item.fieldName}.${index}`,
                                          newValue,
                                        )
                                      }
                                      getOptionLabel={(option) => option}
                                      renderTags={() => null}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          variant="outlined"
                                          placeholder="Tap to Select"
                                          error={
                                            !!errors[`airEmissions.${index}`]
                                          }
                                        />
                                      )}
                                      sx={{
                                        width: "100%",
                                        "& .MuiAutocomplete-inputRoot": {
                                          height: 40,
                                          alignItems: "center",
                                        },
                                        "& .MuiAutocomplete-tag": {
                                          display: "none",
                                        },
                                      }}
                                    />

                                    {errors[`airEmissions.${index}`] && (
                                      <Typography
                                        variant="body2"
                                        color="#D34040"
                                      >
                                        {errors[`airEmissions.${index}`]}
                                      </Typography>
                                    )}

                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        minHeight: "40px",
                                        alignItems: "flex-start",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {formData?.processes[
                                        index
                                      ]?.airEmissions?.map((value) => (
                                        <Chip
                                          key={value}
                                          label={value}
                                          sx={{
                                            bgcolor: "#1C486B",
                                            color: "white",
                                            height: "28px",
                                            m: 0.25,
                                            "& .MuiChip-deleteIcon": {
                                              color: "white",
                                              "&:hover": {
                                                color: "white",
                                              },
                                            },
                                          }}
                                          onDelete={() =>
                                            handleChangeEmission(
                                              `${item.fieldName}.${index}`,
                                              formData?.processes[
                                                index
                                              ]?.airEmissions?.filter(
                                                (v) => v !== value,
                                              ),
                                            )
                                          }
                                        />
                                      ))}
                                    </Box>
                                  </>
                                ) : (
                                  <InputField
                                    title={item.title}
                                    handleChange={handleChange}
                                    placeholder={item.placeholder}
                                    value={
                                      formData?.processes[index]?.processName ||
                                      ""
                                    }
                                    errors={
                                      errors[`${item.fieldName}.${index}`]
                                    }
                                    helperText={
                                      errors[`${item.fieldName}.${index}`]
                                    }
                                    fieldName={`${item.fieldName}.${index}`}
                                    inputSx={{
                                      "& .MuiInputBase-root": {
                                        paddingTop: "0px",
                                        paddingBottom: "0px",
                                        paddingLeft: "0.75rem",
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            ))}
                          </Box>

                          <IconButton
                            sx={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              mt: 4,
                            }}
                            disabled={formData?.processes?.length === 1}
                            onClick={() => handleDelete(index)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      );
                    })}

                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Button
                        disabled={isFilled()}
                        variant="contained"
                        onClick={handleAdd}
                        sx={{
                          mt: "0rem",
                          backgroundColor: "#1C486B",
                          color: "white",
                        }}
                      >
                        <AddIcon />
                        Add New Process
                      </Button>
                    </Box>

                    <Box>
                      {(() => {
                        const massUnits = simpleUnits?.find((u) => u?.Mass)
                          ?.Mass || [
                          "Gram",
                          "Kilogram",
                          "Metric Tonne",
                          "Milligram",
                        ];

                        return (
                          <AddTable
                            inputData={inputTableData}
                            title={"Emission Release in Processes"}
                            table={table || [{}]}
                            formData={formData}
                            setFormData={setFormData}
                            setTable={setTable}
                            units={massUnits}
                            errors={errors}
                            setErrors={setErrorsWithSections}
                          />
                        );
                      })()}
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "10%",
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={addData}
      onClose={handleClose}
    >
      {list()}
    </Drawer>
  );
}

export default Add;
